import React, { useMemo } from 'react';
import { Paper, Stack, Box, Typography, Button, LinearProgress, alpha, useTheme, Chip, Tooltip } from '@mui/material';
import { CheckCircle, Lock, ErrorOutline, ArrowDownward } from '@mui/icons-material';
import { Session, PromotionRun } from '../../types/sessions';
import StatusChip from '../common/StatusChip';

interface RunDashboardHeaderProps {
  run: PromotionRun;
  sourceSession: Session | null;
  targetSession: Session | null;
  totalDecided: number;
  totalUndecided: number;
  totalCommitted: number;
  totalFailed: number;
  onFinalize: () => void;
}

const RunDashboardHeader: React.FC<RunDashboardHeaderProps> = ({
  run, sourceSession, targetSession, totalDecided, totalUndecided, totalCommitted, totalFailed, onFinalize
}) => {
  const theme = useTheme();
  // Total + committed are derived from the same per-class data the sidebar
  // shows. `run.progress.totalInScope` is a snapshot taken at run start; it
  // doesn't track cohort changes (new enrollments mid-run, etc.), so reading
  // it directly made the header diverge from the per-class breakdown.
  const stats = useMemo(() => {
    const total = totalDecided + totalUndecided + totalCommitted;
    const pct = total > 0 ? Math.round((totalCommitted / total) * 1000) / 10 : 0;
    return { total, committed: totalCommitted, pct: Math.min(100, pct) };
  }, [totalDecided, totalUndecided, totalCommitted]);

  const finalizeEnabled = stats.committed > 0 || run.status === 'draft';

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3 },
        borderRadius: 3,
        mb: 3,
        position: 'relative',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
        boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}, 0 12px 32px -12px ${alpha(theme.palette.primary.main, 0.2)}`,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.45)} 100%)`,
        },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 2, md: 3 }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
            <StatusChip status={{ kind: 'runStatus', value: run.status }} />
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
              Promotion run
            </Typography>
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 2 }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            sx={{ mb: 2 }}
          >
            <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: -0.3 }}>
              {sourceSession?.name ?? '…'}
            </Typography>
            <ArrowDownward sx={{ color: 'text.disabled', transform: { xs: 'none', sm: 'rotate(-90deg)' } }} />
            <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: -0.3, color: 'primary.dark' }}>
              {targetSession?.name ?? '…'}
            </Typography>
          </Stack>

          {/* Progress bar */}
          <Box sx={{ maxWidth: 540 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {stats.committed} of {stats.total} committed
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {stats.pct}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={stats.pct}
              sx={{
                height: 7, borderRadius: 4,
                bgcolor: alpha(theme.palette.primary.main, 0.10),
                '& .MuiLinearProgress-bar': { borderRadius: 4 }
              }}
            />

            <Stack direction="row" spacing={1.5} sx={{ mt: 1.25, flexWrap: 'wrap' }}>
              {totalUndecided > 0 && (
                <Tooltip title="Students with no decision yet">
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<ErrorOutline sx={{ fontSize: '0.9rem !important' }} />}
                    label={`${totalUndecided} undecided`}
                    sx={{ fontWeight: 500 }}
                  />
                </Tooltip>
              )}
              {totalDecided > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  color="primary"
                  label={`${totalDecided} ready to commit`}
                  sx={{ fontWeight: 500 }}
                />
              )}
              {totalFailed > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  color="error"
                  icon={<ErrorOutline sx={{ fontSize: '0.9rem !important' }} />}
                  label={`${totalFailed} failed (retry)`}
                  sx={{ fontWeight: 500 }}
                />
              )}
            </Stack>
          </Box>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flexShrink: 0 }}
        >
          <Tooltip title={finalizeEnabled ? '' : 'Commit at least one chunk to enable'}>
            <span>
              <Button
                variant="contained"
                disableElevation
                startIcon={<Lock />}
                disabled={!finalizeEnabled || run.status === 'committed' || run.status === 'cancelled'}
                onClick={onFinalize}
              >
                {run.status === 'committed' ? 'Closed' : 'Finalize run'}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default RunDashboardHeader;
